/**
 * SpaceDetailsPage — "/spaces/:spaceId" (mockup: Overview / Tasks / Members /
 * Files / Conversations / Settings). Backed entirely by the existing
 * spacesAPI + communityAPI — no new backend surface. The Conversations tab
 * embeds SpaceFeed — the existing spaceId-scoped feed component (same one
 * RunDrawer uses for its own space view) — rather than the general
 * ConversationsPage hub, so it only shows this space's own thread instead of
 * every chat the user has anywhere in the app.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Rocket, Users, ListChecks, FileText, MoreHorizontal, Pencil, Plus,
  Search, X, CheckCircle2, UserPlus, LogOut, Trash2, ChevronRight,
  UserMinus, Upload, Download, Bot, ShieldCheck, Loader2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { spacesAPI, communityAPI, API_ORIGIN } from '../services/api.js';
import Avatar from '../components/Avatar.tsx';
import SpaceFeed from '../components/SpaceFeed.jsx';
import AddTaskModal from '../components/modals/AddTaskModal.tsx';
import TeamAgentsPanel from '../components/spaces/TeamAgentsPanel.jsx';

const T = {
  page: 'var(--lyra-color-surface)',
  surface: 'var(--lyra-color-surface)',
  surface2: 'var(--lyra-color-surface-2)',
  border: 'var(--lyra-color-border)',
  ink: 'var(--lyra-color-text)',
  ink2: 'var(--app-text-2)',
  ink3: 'var(--lyra-color-text-muted)',
  ink4: 'var(--app-text-3)',
  accent: 'var(--lyra-color-primary)',
  accentLight: 'var(--app-primary-soft)',
  accentDark: 'var(--app-primary-deep)',
  green: 'var(--lyra-color-success)',
  greenBg: 'var(--lyra-color-success-bg)',
  amber: 'var(--lyra-color-warning)',
  amberBg: 'var(--lyra-color-warning-bg)',
  red: 'var(--lyra-color-danger)',
  redBg: 'var(--lyra-color-danger-bg)',
  violet: 'var(--lyra-color-primary)',
  violetBg: 'var(--app-primary-soft)',
};

const TABS = ['Overview', 'Tasks', 'Members', 'Agents', 'Files', 'Conversations', 'Settings'] as const;
type Tab = typeof TABS[number];

const STATUS_META: Record<string, { label: string; color: string; bg: string }> = {
  active: { label: 'On track', color: T.green, bg: T.greenBg },
  idle: { label: 'Idle', color: T.ink3, bg: T.surface2 },
  paused: { label: 'Paused', color: T.amber, bg: T.amberBg },
};

function timeAgo(d?: string | Date | null) {
  if (!d) return '';
  const ms = Date.now() - new Date(d).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function activityLine(a: any) {
  if (a.type === 'task_event') return `${a.taskEvent?.kind === 'complete' ? 'Completed' : a.taskEvent?.kind === 'fail' ? 'Failed' : 'Updated'} "${a.taskEvent?.taskName || 'a task'}"`;
  if (a.type === 'member_event') return a.memberEvent?.kind === 'join' ? 'Joined the space' : 'Left the space';
  return a.content || '';
}

const Card: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 18, ...style }}>{children}</div>
);

export default function SpaceDetailsPage() {
  const { spaceId } = useParams<{ spaceId: string }>();
  const navigate = useNavigate();

  const [space, setSpace] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // A link from a notification can name the tab (/spaces/:id?tab=Conversations).
  const [tab, setTab] = useState<Tab>(() => {
    const want = new URLSearchParams(window.location.search).get('tab');
    return (TABS as readonly string[]).includes(want || '') ? (want as Tab) : 'Overview';
  });
  const [activity, setActivity] = useState<any[]>([]);
  const [onlineIds, setOnlineIds] = useState<Set<string>>(new Set());
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [autoPilotBusy, setAutoPilotBusy] = useState(false);
  const [myConnectors, setMyConnectors] = useState<Array<{ name: string; label: string; description: string }>>([]);
  const [connectorsBusy, setConnectorsBusy] = useState(false);

  const [showInvite, setShowInvite] = useState(false);
  const [candidates, setCandidates] = useState<any[]>([]);
  const [inviteQuery, setInviteQuery] = useState('');
  const [selectedInvites, setSelectedInvites] = useState<Set<string>>(new Set());
  const [invitingBusy, setInvitingBusy] = useState(false);
  const [inviteRole, setInviteRole] = useState<'member' | 'manager'>('member');

  const [editingAbout, setEditingAbout] = useState(false);
  const [aboutDraft, setAboutDraft] = useState('');

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [nameSaving, setNameSaving] = useState(false);

  // ── Kanban-synced Space tasks ──────────────────────────────────────────────
  const [kanbanTasks, setKanbanTasks] = useState<any[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);

  // ── Files ───────────────────────────────────────────────────────────────────
  const [files, setFiles] = useState<any[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  const canManage = !!space?.canManage;
  const isOwner = !!space?.isOwner;

  const loadKanbanTasks = useCallback(async () => {
    if (!spaceId) return;
    setTasksLoading(true);
    try {
      const res: any = await spacesAPI.listKanbanTasks(spaceId);
      setKanbanTasks(Array.isArray(res?.tasks) ? res.tasks : []);
    } catch (_) {
      setKanbanTasks([]);
    } finally {
      setTasksLoading(false);
    }
  }, [spaceId]);

  const loadFiles = useCallback(async () => {
    if (!spaceId) return;
    setFilesLoading(true);
    try {
      const res: any = await spacesAPI.listFiles(spaceId);
      setFiles(Array.isArray(res?.files) ? res.files : []);
    } catch (_) {
      setFiles([]);
    } finally {
      setFilesLoading(false);
    }
  }, [spaceId]);

  const loadSpace = useCallback(async () => {
    if (!spaceId) return;
    setLoading(true);
    setNotFound(false);
    try {
      const res: any = await spacesAPI.get(spaceId);
      setSpace(res?.space || null);
      setAboutDraft(res?.space?.description || '');
    } catch (err: any) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [spaceId]);

  useEffect(() => { loadSpace(); }, [loadSpace]);

  useEffect(() => {
    if (!spaceId || !space?.isMember) return;
    spacesAPI.getActivity(spaceId, { limit: 10 }).then((res: any) => {
      setActivity(Array.isArray(res?.activities) ? [...res.activities].reverse() : []);
    }).catch(() => setActivity([]));
  }, [spaceId, space?.isMember]);

  useEffect(() => {
    communityAPI.getOnlineMembers().then((res: any) => {
      const list = Array.isArray(res?.members) ? res.members : [];
      setOnlineIds(new Set(list.map((m: any) => String(m.userId))));
    }).catch(() => setOnlineIds(new Set()));
  }, []);

  // Auto-pilot can self-pause server-side once its turn cap is hit — poll
  // lightly (only while it's on) so the header toggle reflects that without
  // needing a new SSE event type.
  useEffect(() => {
    if (!spaceId || !space?.autoPilot) return;
    const t = window.setInterval(() => {
      spacesAPI.get(spaceId).then((res: any) => { if (res?.space) setSpace(res.space); }).catch(() => {});
    }, 8000);
    return () => window.clearInterval(t);
  }, [spaceId, space?.autoPilot]);

  // Load tasks/files lazily — once membership is known and the relevant tab is visited
  useEffect(() => {
    if (!space?.isMember) return;
    if ((tab === 'Tasks' || tab === 'Overview') && kanbanTasks.length === 0 && !tasksLoading) loadKanbanTasks();
    if ((tab === 'Files' || tab === 'Overview') && files.length === 0 && !filesLoading) loadFiles();
  }, [tab, space?.isMember]); // eslint-disable-line react-hooks/exhaustive-deps

  const removeMember = async (targetUserId: string, name: string) => {
    if (!spaceId) return;
    if (!window.confirm(`Remove ${name} from this space?`)) return;
    try {
      const res: any = await spacesAPI.removeMember(spaceId, targetUserId);
      setSpace(res?.space);
      toast.success(`${name} removed`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not remove member');
    }
  };

  const setMemberRole = async (targetUserId: string, role: 'manager' | 'member') => {
    if (!spaceId) return;
    try {
      const res: any = await spacesAPI.setMemberRole(spaceId, targetUserId, role);
      setSpace(res?.space);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not change role');
    }
  };

  const cancelInvite = async (inviteId: string, name: string) => {
    if (!spaceId) return;
    if (!window.confirm(`Cancel the invite sent to ${name}?`)) return;
    try {
      const res: any = await spacesAPI.cancelInvite(spaceId, inviteId);
      setSpace(res?.space);
      toast.success('Invite cancelled');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not cancel invite');
    }
  };

  const handleUploadFile = async (fileList: FileList | null) => {
    if (!spaceId || !fileList || fileList.length === 0) return;
    setUploadingFile(true);
    try {
      await spacesAPI.uploadFile(spaceId, fileList[0]);
      await loadFiles();
      toast.success('File uploaded');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const deleteFile = async (fileId: string) => {
    if (!spaceId) return;
    if (!window.confirm('Remove this file?')) return;
    try {
      await spacesAPI.deleteFile(spaceId, fileId);
      setFiles((prev) => prev.filter((f) => f._id !== fileId));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not remove file');
    }
  };

  const openInvite = async () => {
    setShowInvite(true);
    setSelectedInvites(new Set());
    setInviteRole('member');
    try {
      const res: any = await communityAPI.getMembers();
      const list = res?.members || res?.lyrans || (Array.isArray(res) ? res : []);
      setCandidates(list);
    } catch (_) {
      setCandidates([]);
    }
  };

  const memberIds = useMemo(() => new Set((space?.members || []).map((m: any) => String(m.userId))), [space]);
  const filteredCandidates = useMemo(() => candidates.filter((c: any) => {
    if (memberIds.has(String(c.userId || c._id))) return false;
    const q = inviteQuery.toLowerCase();
    if (!q) return true;
    return (c.name || '').toLowerCase().includes(q) || (c.lyran_name || '').toLowerCase().includes(q);
  }), [candidates, memberIds, inviteQuery]);

  const sendInvites = async () => {
    if (selectedInvites.size === 0 || invitingBusy || !spaceId) return;
    setInvitingBusy(true);
    try {
      await Promise.all(Array.from(selectedInvites).map((uid) => spacesAPI.invite(spaceId, uid, inviteRole)));
      toast.success('Invite sent');
      setShowInvite(false);
      loadSpace();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not send invites');
    } finally {
      setInvitingBusy(false);
    }
  };

  const saveAbout = async () => {
    if (!spaceId) return;
    try {
      const res: any = await spacesAPI.update(spaceId, { description: aboutDraft });
      setSpace(res?.space);
      setEditingAbout(false);
      toast.success('Space updated');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not update space');
    }
  };

  const changeStatus = async (status: string) => {
    if (!spaceId) return;
    try {
      const res: any = await spacesAPI.update(spaceId, { status });
      setSpace(res?.space);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not update status');
    }
  };

  const changeVisibility = async (visibility: string) => {
    if (!spaceId) return;
    try {
      const res: any = await spacesAPI.update(spaceId, { visibility });
      setSpace(res?.space);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not update visibility');
    }
  };

  const saveName = async () => {
    const trimmed = nameDraft.trim();
    if (!spaceId || !trimmed || nameSaving) return;
    if (trimmed === space.name) { setEditingName(false); return; }
    setNameSaving(true);
    try {
      const res: any = await spacesAPI.update(spaceId, { name: trimmed });
      setSpace(res?.space);
      setEditingName(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not rename the team');
    } finally {
      setNameSaving(false);
    }
  };

  // Connectors the viewer has connected themself (Home → Connectors): what they can switch on for the team.
  useEffect(() => {
    if (tab !== 'Settings') return;
    spacesAPI.availableConnectors().then((r: any) => setMyConnectors(Array.isArray(r?.connectors) ? r.connectors : [])).catch(() => setMyConnectors([]));
  }, [tab]);

  const toggleConnector = async (name: string) => {
    if (!spaceId || !space || connectorsBusy) return;
    const current: string[] = space.connectors || [];
    const next = current.includes(name) ? current.filter((n) => n !== name) : [...current, name];
    setConnectorsBusy(true);
    try {
      const res: any = await spacesAPI.update(spaceId, { connectors: next });
      setSpace(res?.space);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not update connectors');
    } finally {
      setConnectorsBusy(false);
    }
  };

  const updateAutoPilot = async (payload: { autoPilot?: boolean; autoPilotCap?: number }) => {
    if (!spaceId) return;
    setAutoPilotBusy(true);
    try {
      const res: any = await spacesAPI.update(spaceId, payload);
      setSpace(res?.space);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not update auto-pilot');
    } finally {
      setAutoPilotBusy(false);
    }
  };

  const handleJoin = async () => {
    if (!spaceId || busy) return;
    setBusy(true);
    try {
      await spacesAPI.join(spaceId);
      toast.success(`Joined ${space?.name}`);
      loadSpace();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not join this space');
    } finally {
      setBusy(false);
    }
  };

  const handleLeave = async () => {
    if (!spaceId || busy) return;
    if (!window.confirm(`Leave "${space?.name}"?`)) return;
    setBusy(true);
    try {
      await spacesAPI.leave(spaceId);
      toast.success('You left the space');
      navigate('/teams');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not leave the space');
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!spaceId || busy) return;
    if (!window.confirm(`Delete "${space?.name}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await spacesAPI.delete(spaceId);
      toast.success('Space deleted');
      navigate('/teams');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not delete the space');
      setBusy(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '80px 0', textAlign: 'center', color: T.ink3, fontSize: 13 }}>Loading space…</div>;
  }
  if (notFound || !space) {
    return (
      <div style={{ padding: '60px 32px', textAlign: 'center' }}>
        <Rocket size={30} color={T.ink4} style={{ margin: '0 auto 12px' }} />
        <div style={{ fontSize: 14, fontWeight: 600, color: T.ink2, marginBottom: 10 }}>Space not found</div>
        <button onClick={() => navigate('/teams')} style={{ border: `1px solid ${T.border}`, background: T.surface, borderRadius: 8, padding: '8px 16px', fontSize: 12.5, cursor: 'pointer' }}>
          Back to Spaces
        </button>
      </div>
    );
  }

  const statusMeta = STATUS_META[space.status] || STATUS_META.idle;
  const fileCount = files.length;

  return (
    <div style={{ background: T.page, minHeight: '100%', padding: '28px 32px 60px' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        {/* Breadcrumb */}
        <div style={{ fontSize: 12, color: T.ink3, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
          <button onClick={() => navigate('/teams')} style={{ border: 'none', background: 'none', color: T.ink3, cursor: 'pointer', padding: 0, fontSize: 12 }}>Teams</button>
          <ChevronRight size={12} /> <span style={{ color: T.ink2, fontWeight: 600 }}>{space.name}</span>
        </div>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', minWidth: 0 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: T.violetBg, color: T.violet, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Rocket size={20} />
            </div>
            <div style={{ minWidth: 0 }}>
              {editingName ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    autoFocus
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEditingName(false); }}
                    style={{ fontSize: 20, fontWeight: 700, color: T.ink, border: `1px solid ${T.border}`, borderRadius: 8, padding: '2px 8px', minWidth: 200 }}
                  />
                  <button onClick={saveName} disabled={nameSaving || !nameDraft.trim()}
                    style={{ border: 'none', background: T.accentDark, color: 'var(--app-on-accent)', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    {nameSaving ? 'Saving…' : 'Save'}
                  </button>
                  <button onClick={() => setEditingName(false)}
                    style={{ border: `1px solid ${T.border}`, background: T.surface, color: T.ink2, borderRadius: 8, padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: T.ink }}>{space.name}</div>
                  {canManage && (
                    <button onClick={() => { setNameDraft(space.name || ''); setEditingName(true); }} title="Rename team"
                      style={{ border: 'none', background: 'none', color: T.ink3, cursor: 'pointer', display: 'flex', padding: 2 }}>
                      <Pencil size={14} />
                    </button>
                  )}
                </div>
              )}
              <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 3, maxWidth: 520 }}>{space.description || 'No description yet.'}</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, flexShrink: 0, position: 'relative' }}>
            {space.isMember ? (
              canManage && (
                <button onClick={openInvite}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, border: `1px solid ${T.border}`, background: T.surface, color: T.ink2, borderRadius: 9, padding: '9px 15px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
                  <UserPlus size={13} /> Invite
                </button>
              )
            ) : (
              <button onClick={handleJoin} disabled={busy}
                style={{ display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: T.accentDark, color: 'var(--app-on-accent)', borderRadius: 9, padding: '9px 15px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
                Join space
              </button>
            )}
            {space.isMember && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <select
                  value={space.autoPilotCap || 6}
                  onChange={(e) => updateAutoPilot({ autoPilotCap: Number(e.target.value) })}
                  disabled={autoPilotBusy}
                  style={{ border: `1px solid ${T.border}`, background: T.surface, borderRadius: 8, padding: '7px 8px', fontSize: 11.5, color: T.ink2, cursor: autoPilotBusy ? 'not-allowed' : 'pointer' }}
                >
                  {[3, 6, 10, 50, 100].map((n) => <option key={n} value={n}>{n} turns</option>)}
                </select>
                <button
                  onClick={() => updateAutoPilot({ autoPilot: !space.autoPilot })}
                  disabled={autoPilotBusy}
                  title={space.autoPilot ? 'Auto-pilot on — Lyrans are continuing the conversation' : 'Auto-pilot off'}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, border: `1px solid ${space.autoPilot ? T.accentDark : T.border}`, background: space.autoPilot ? T.greenBg : T.surface, color: space.autoPilot ? T.accentDark : T.ink2, borderRadius: 9, padding: '9px 12px', fontSize: 12.5, fontWeight: 600, cursor: autoPilotBusy ? 'not-allowed' : 'pointer' }}
                >
                  <Bot size={13} /> Auto-pilot {space.autoPilot ? 'On' : 'Off'}
                </button>
              </div>
            )}
            {space.isMember && (
              <>
                <button onClick={() => setMenuOpen((v) => !v)}
                  style={{ width: 36, height: 36, border: `1px solid ${T.border}`, background: T.surface, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                  <MoreHorizontal size={16} color={T.ink3} />
                </button>
                {menuOpen && (
                  <div style={{ position: 'absolute', top: '110%', right: 0, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: 6, minWidth: 170, zIndex: 6, boxShadow: '0 10px 30px rgba(0,0,0,.08)' }}>
                    <button onClick={() => { setMenuOpen(false); setTab('Settings'); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', border: 'none', background: 'none', padding: '8px 10px', borderRadius: 7, fontSize: 12.5, color: T.ink2, cursor: 'pointer' }}>
                      <Pencil size={13} /> Space settings
                    </button>
                    <button onClick={() => { setMenuOpen(false); handleLeave(); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', border: 'none', background: 'none', padding: '8px 10px', borderRadius: 7, fontSize: 12.5, color: T.red, cursor: 'pointer' }}>
                      <LogOut size={13} /> Leave space
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Tabs — the active indicator is its own line under the buttons, not the
            container's border, so it can't get visually swallowed by it. */}
        <div style={{ position: 'relative', display: 'flex', gap: 4, marginBottom: 20, overflowX: 'auto' }}>
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 1, background: T.border }} />
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)}
              style={{
                position: 'relative', border: 'none', background: 'none', padding: '9px 14px 8px', fontSize: 12.5,
                fontWeight: tab === t ? 700 : 500, cursor: 'pointer', whiteSpace: 'nowrap',
                color: tab === t ? T.accentDark : T.ink3,
                borderBottom: tab === t ? `2.5px solid ${T.accentDark}` : '2.5px solid transparent',
              }}>
              {t}
            </button>
          ))}
        </div>

        {tab === 'Overview' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 18 }}>
              <Card style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: T.accentLight, color: T.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Users size={16} /></div>
                <div><div style={{ fontSize: 18, fontWeight: 700, color: T.ink }}>{space.memberCount ?? (space.members || []).length}</div><div style={{ fontSize: 10.5, color: T.ink4 }}>AI members</div></div>
              </Card>
              <Card style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--app-primary-soft)', color: 'var(--lyra-color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><ListChecks size={16} /></div>
                <div><div style={{ fontSize: 18, fontWeight: 700, color: T.ink }}>{kanbanTasks.length}</div><div style={{ fontSize: 10.5, color: T.ink4 }}>Tasks</div></div>
              </Card>
              <Card style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: T.violetBg, color: T.violet, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><FileText size={16} /></div>
                <div><div style={{ fontSize: 18, fontWeight: 700, color: T.ink }}>{fileCount}</div><div style={{ fontSize: 10.5, color: T.ink4 }}>Files</div></div>
              </Card>
              <Card style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, color: statusMeta.color, background: statusMeta.bg, borderRadius: 999, padding: '6px 14px' }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusMeta.color }} /> {statusMeta.label}
                </span>
              </Card>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 16 }}>
              <Card>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>About this space</div>
                  {space.isOwner && !editingAbout && (
                    <button onClick={() => setEditingAbout(true)} style={{ display: 'flex', alignItems: 'center', gap: 4, border: 'none', background: 'none', color: T.accentDark, fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}>
                      <Pencil size={11} /> Edit
                    </button>
                  )}
                </div>
                {editingAbout ? (
                  <div style={{ marginBottom: 16 }}>
                    <textarea value={aboutDraft} onChange={(e) => setAboutDraft(e.target.value)} rows={3} maxLength={280}
                      style={{ width: '100%', border: `1px solid ${T.border}`, borderRadius: 8, padding: 10, fontSize: 12.5, color: T.ink, fontFamily: 'inherit', resize: 'vertical' }} />
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button onClick={saveAbout} style={{ border: 'none', background: T.accentDark, color: 'var(--app-on-accent)', borderRadius: 7, padding: '6px 14px', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}>Save</button>
                      <button onClick={() => { setEditingAbout(false); setAboutDraft(space.description || ''); }} style={{ border: `1px solid ${T.border}`, background: T.surface, borderRadius: 7, padding: '6px 14px', fontSize: 11.5, cursor: 'pointer' }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 12.5, color: T.ink3, lineHeight: 1.5, marginBottom: 16 }}>{space.description || 'No description yet.'}</div>
                )}

                <div style={{ fontSize: 12.5, fontWeight: 700, color: T.ink, marginBottom: 10 }}>Members</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
                  {(space.members || []).map((m: any) => (
                    <div key={m.userId} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ position: 'relative' }}>
                        <Avatar src={m.lyranAvatar} name={m.lyranName || 'Member'} size={30} />
                        {onlineIds.has(String(m.userId)) && (
                          <span style={{ position: 'absolute', bottom: -1, right: -1, width: 8, height: 8, borderRadius: '50%', background: T.green, border: '2px solid var(--lyra-color-surface)' }} />
                        )}
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>{m.lyranName || 'Member'}</div>
                        <div style={{ fontSize: 11, color: T.ink4, textTransform: 'capitalize' }}>{m.role}</div>
                      </div>
                    </div>
                  ))}
                </div>
                {canManage && (
                  <button onClick={openInvite} style={{ display: 'flex', alignItems: 'center', gap: 6, border: `1px solid ${T.border}`, background: T.surface2, color: T.ink2, borderRadius: 8, padding: '7px 14px', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}>
                    <Plus size={12} /> Add member
                  </button>
                )}
              </Card>

              <Card>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>Recent activity</div>
                  <button onClick={() => setTab('Conversations')} style={{ border: 'none', background: 'none', color: T.accentDark, fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}>View all →</button>
                </div>
                {!space.isMember ? (
                  <div style={{ fontSize: 12, color: T.ink4 }}>Join this space to see activity.</div>
                ) : activity.length === 0 ? (
                  <div style={{ fontSize: 12, color: T.ink4 }}>No recent activity.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {activity.map((a: any) => (
                      <div key={a._id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                        <Avatar src={a.lyranAvatar} name={a.lyranName || 'System'} size={26} />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>{a.lyranName || 'System'}</div>
                          <div style={{ fontSize: 11.5, color: T.ink3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activityLine(a)}</div>
                        </div>
                        <div style={{ fontSize: 10.5, color: T.ink4, flexShrink: 0 }}>{timeAgo(a.createdAt)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </>
        )}

        {tab === 'Tasks' && (
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>Tasks</div>
              {canManage && (
                <button onClick={() => setShowAddTask(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: T.accentDark, color: 'var(--app-on-accent)', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  <Plus size={13} /> Add task
                </button>
              )}
            </div>
            {tasksLoading ? (
              <div style={{ padding: 30, textAlign: 'center' }}><Loader2 size={18} color={T.ink4} style={{ animation: 'spin 1s linear infinite' }} /></div>
            ) : kanbanTasks.length === 0 ? (
              <div style={{ padding: 30, textAlign: 'center', fontSize: 12.5, color: T.ink4 }}>No tasks yet.</div>
            ) : (
              kanbanTasks.map((t: any) => (
                <div key={t._id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: `1px solid ${T.border}` }}>
                  <CheckCircle2 size={15} color={t.status === 'Done' ? T.green : T.ink4} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>{t.title}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3, flexWrap: 'wrap' }}>
                      {(Array.isArray(t.agentIds) && t.agentIds.length ? t.agentIds : (t.agentId ? [t.agentId] : [])).map((a: any) => (
                        a?.name && (
                          <span key={a._id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, color: T.violet, background: T.violetBg, borderRadius: 999, padding: '2px 8px' }}>
                            <Bot size={10} /> {a.name}
                          </span>
                        )
                      ))}
                      {t.createdBy?.name && (
                        <span style={{ fontSize: 10.5, color: T.ink4 }}>by {t.createdBy.name}</span>
                      )}
                    </div>
                  </div>
                  <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'capitalize', color: T.ink3, background: T.surface2, borderRadius: 999, padding: '3px 10px' }}>{t.status}</span>
                </div>
              ))
            )}
          </Card>
        )}

        {tab === 'Members' && (
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            {(space.members || []).map((m: any) => {
              const mid = String(m.userId);
              const isSelf = mid === String(space.viewerId || '');
              // A manager can only remove plain members; the owner can remove anyone but themselves.
              const canRemoveThis = canManage && !isSelf && (isOwner || m.role === 'member');
              return (
                <div key={m.userId} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: `1px solid ${T.border}` }}>
                  <div style={{ position: 'relative' }}>
                    <Avatar src={m.lyranAvatar} name={m.lyranName || 'Member'} size={32} />
                    {onlineIds.has(mid) && (
                      <span style={{ position: 'absolute', bottom: -1, right: -1, width: 8, height: 8, borderRadius: '50%', background: T.green, border: '2px solid var(--lyra-color-surface)' }} />
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>{m.lyranName || 'Member'}</div>
                    <div style={{ fontSize: 11, color: T.ink4 }}>Joined {new Date(m.joinedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                  </div>
                  {isOwner && m.role !== 'owner' ? (
                    <select
                      value={m.role}
                      onChange={(e) => setMemberRole(mid, e.target.value as 'manager' | 'member')}
                      style={{ fontSize: 11, fontWeight: 700, textTransform: 'capitalize', color: T.ink2, background: T.surface2, border: `1px solid ${T.border}`, borderRadius: 999, padding: '3px 10px', cursor: 'pointer' }}
                    >
                      <option value="member">Member</option>
                      <option value="manager">Manager</option>
                    </select>
                  ) : (
                    <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'capitalize', color: m.role === 'owner' ? T.accentDark : T.ink3, background: m.role === 'owner' ? T.accentLight : T.surface2, borderRadius: 999, padding: '3px 10px' }}>{m.role}</span>
                  )}
                  {canRemoveThis && (
                    <button onClick={() => removeMember(mid, m.lyranName || 'Member')} title="Remove member"
                      style={{ border: 'none', background: 'none', color: T.red, cursor: 'pointer', display: 'flex', padding: 4 }}>
                      <UserMinus size={14} />
                    </button>
                  )}
                </div>
              );
            })}
            {canManage && (
              <div style={{ padding: 14 }}>
                <button onClick={openInvite} style={{ display: 'flex', alignItems: 'center', gap: 6, border: `1px solid ${T.border}`, background: T.surface2, color: T.ink2, borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  <Plus size={13} /> Add member
                </button>
              </div>
            )}
          </Card>
        )}

        {tab === 'Members' && canManage && (space.invites || []).length > 0 && (
          <Card style={{ padding: 0, overflow: 'hidden', marginTop: 16 }}>
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${T.border}`, fontSize: 12.5, fontWeight: 700, color: T.ink }}>
              Pending invites
            </div>
            {(space.invites || []).map((inv: any) => (
              <div key={inv._id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: `1px solid ${T.border}` }}>
                <Avatar src={inv.lyranAvatar} name={inv.lyranName || 'Invited'} size={32} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>{inv.lyranName || 'Pending invite'}</div>
                  <div style={{ fontSize: 11, color: T.ink4 }}>Invited {new Date(inv.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                </div>
                <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'capitalize', color: T.ink3, background: T.surface2, borderRadius: 999, padding: '3px 10px' }}>Pending</span>
                <button onClick={() => cancelInvite(inv._id, inv.lyranName || 'this person')} title="Cancel invite"
                  style={{ border: 'none', background: 'none', color: T.red, cursor: 'pointer', display: 'flex', padding: 4 }}>
                  <X size={14} />
                </button>
              </div>
            ))}
          </Card>
        )}

        {tab === 'Agents' && space && <TeamAgentsPanel spaceId={spaceId as string} meId={String(space.viewerId || '')} />}

        {tab === 'Files' && (
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>Files</div>
              {canManage && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: T.accentDark, color: 'var(--app-on-accent)', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: uploadingFile ? 'not-allowed' : 'pointer', opacity: uploadingFile ? 0.7 : 1 }}>
                  {uploadingFile ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Upload size={13} />}
                  {uploadingFile ? 'Uploading…' : 'Upload'}
                  <input type="file" onChange={(e) => handleUploadFile(e.target.files)} disabled={uploadingFile} style={{ display: 'none' }} />
                </label>
              )}
            </div>
            {filesLoading ? (
              <div style={{ padding: 30, textAlign: 'center' }}><Loader2 size={18} color={T.ink4} style={{ animation: 'spin 1s linear infinite' }} /></div>
            ) : files.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 36 }}>
                <FileText size={28} color={T.ink4} style={{ margin: '0 auto 12px' }} />
                <div style={{ fontSize: 13.5, fontWeight: 600, color: T.ink2, marginBottom: 4 }}>No files yet</div>
                <div style={{ fontSize: 12, color: T.ink4 }}>{canManage ? 'Upload a file, or attach one in Conversations.' : 'A manager can upload files here.'}</div>
              </div>
            ) : (
              files.map((f: any) => (
                <div key={f._id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: `1px solid ${T.border}` }}>
                  <FileText size={16} color={T.ink4} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.originalName}</div>
                    <div style={{ fontSize: 11, color: T.ink4, marginTop: 2 }}>{f.uploadedBy?.name || 'Someone'} · {timeAgo(f.createdAt)}</div>
                  </div>
                  <a href={`${API_ORIGIN}${f.url}`} target="_blank" rel="noopener noreferrer" title="Download" style={{ color: T.ink3, display: 'flex', padding: 4 }}>
                    <Download size={14} />
                  </a>
                  {canManage && (
                    <button onClick={() => deleteFile(f._id)} title="Remove file" style={{ border: 'none', background: 'none', color: T.red, cursor: 'pointer', display: 'flex', padding: 4 }}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))
            )}
          </Card>
        )}

        {tab === 'Conversations' && (
          <div style={{ margin: '-24px -32px 0', height: 'calc(100vh - 260px)', minHeight: 480, border: `1px solid ${T.border}`, borderLeft: 'none', borderRight: 'none' }}>
            <SpaceFeed spaceId={spaceId} embedded />
          </div>
        )}

        {tab === 'Settings' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 560 }}>
            <Card>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink, marginBottom: 14 }}>Space settings</div>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: T.ink4, marginBottom: 6 }}>Description</div>
                <textarea
                  value={aboutDraft}
                  onChange={(e) => setAboutDraft(e.target.value)}
                  disabled={!space.isOwner}
                  rows={3}
                  maxLength={280}
                  style={{ width: '100%', border: `1px solid ${T.border}`, borderRadius: 8, padding: 10, fontSize: 12.5, color: T.ink, fontFamily: 'inherit', resize: 'vertical', background: space.isOwner ? 'var(--lyra-color-surface)' : T.surface2 }}
                />
                {space.isOwner && (
                  <button onClick={saveAbout} style={{ marginTop: 8, border: 'none', background: T.accentDark, color: 'var(--app-on-accent)', borderRadius: 7, padding: '6px 14px', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}>Save</button>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12.5, paddingTop: 10, borderTop: `1px solid ${T.border}`, marginBottom: 10 }}>
                <span style={{ color: T.ink3 }}>Visibility</span>
                <select value={space.visibility} onChange={(e) => changeVisibility(e.target.value)} disabled={!space.isOwner}
                  style={{ border: `1px solid ${T.border}`, borderRadius: 7, padding: '5px 10px', fontSize: 12, color: T.ink2, background: 'var(--lyra-color-surface)' }}>
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12.5 }}>
                <span style={{ color: T.ink3 }}>Status</span>
                <select value={space.status} onChange={(e) => changeStatus(e.target.value)} disabled={!space.isOwner}
                  style={{ border: `1px solid ${T.border}`, borderRadius: 7, padding: '5px 10px', fontSize: 12, color: T.ink2, background: 'var(--lyra-color-surface)' }}>
                  <option value="active">Active (On track)</option>
                  <option value="idle">Idle</option>
                  <option value="paused">Paused</option>
                </select>
              </div>
            </Card>

            <Card>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink, marginBottom: 4 }}>Connectors</div>
              <div style={{ fontSize: 12, color: T.ink3, marginBottom: 12, lineHeight: 1.5 }}>
                Connectors switched on here are available in this team's conversations. They run with the access of the person who posts, so everyone keeps their own permissions.
              </div>
              {(() => {
                const on: string[] = space.connectors || [];
                const mineNames = new Set(myConnectors.map((c) => c.name));
                const rows = [
                  ...myConnectors.map((c) => ({ name: c.name, label: c.label, description: c.description, mine: true })),
                  ...on.filter((n) => !mineNames.has(n)).map((n) => ({ name: n, label: n, description: '', mine: false })),
                ];
                if (rows.length === 0) return <div style={{ fontSize: 12, color: T.ink4 }}>You have no connected agents yet. Connect one from Home → Connectors, then switch it on here.</div>;
                return rows.map((c) => {
                  const enabled = on.includes(c.name);
                  const canToggle = !!space.canManage && !connectorsBusy && (c.mine || enabled);
                  return (
                    <label key={c.name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: `1px solid ${T.border}`, cursor: canToggle ? 'pointer' : 'default', opacity: canToggle || enabled ? 1 : 0.7 }}>
                      <input type="checkbox" checked={enabled} disabled={!canToggle} onChange={() => toggleConnector(c.name)} />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: T.ink }}>{c.label}</span>
                        {c.description && <span style={{ display: 'block', fontSize: 11.5, color: T.ink3 }}>{c.description}</span>}
                      </span>
                      {!c.mine && <span style={{ fontSize: 11, color: T.ink4 }}>Not connected by you</span>}
                    </label>
                  );
                });
              })()}
              {!space.canManage && <div style={{ fontSize: 11.5, color: T.ink4, marginTop: 8 }}>Only the owner or a manager can change this.</div>}
            </Card>

            <Card style={{ borderColor: 'var(--app-danger-border)' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.red, marginBottom: 6 }}>Danger zone</div>
              <div style={{ fontSize: 12, color: T.ink3, marginBottom: 12 }}>
                {space.isOwner ? 'Deleting this space cannot be undone.' : 'Leaving removes you from this space.'}
              </div>
              {space.isOwner ? (
                <button onClick={handleDelete} disabled={busy}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: T.red, color: 'var(--app-on-accent)', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer' }}>
                  <Trash2 size={13} /> Delete space
                </button>
              ) : (
                <button onClick={handleLeave} disabled={busy}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: T.red, color: 'var(--app-on-accent)', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer' }}>
                  <LogOut size={13} /> Leave space
                </button>
              )}
            </Card>
          </div>
        )}
      </div>

      {/* Invite modal */}
      {showInvite && (
        <div onClick={(e) => { if (e.target === e.currentTarget) setShowInvite(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,10,10,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ width: 480, maxHeight: '78vh', background: T.surface, borderRadius: 14, border: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>Invite to {space.name}</div>
              <button onClick={() => setShowInvite(false)} style={{ border: 'none', background: 'none', cursor: 'pointer' }}><X size={16} color={T.ink3} /></button>
            </div>
            <div style={{ padding: '10px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, border: `1px solid ${T.border}`, borderRadius: 9, padding: '7px 12px' }}>
                <Search size={13} color={T.ink4} />
                <input autoFocus value={inviteQuery} onChange={(e) => setInviteQuery(e.target.value)} placeholder="Search people to invite…"
                  style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 12.5, width: '100%', fontFamily: 'inherit' }} />
              </div>
              {isOwner && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                  <span style={{ fontSize: 11.5, color: T.ink3 }}>Invite as</span>
                  <div style={{ display: 'flex', border: `1px solid ${T.border}`, borderRadius: 8, padding: 2 }}>
                    {(['member', 'manager'] as const).map((r) => (
                      <button key={r} onClick={() => setInviteRole(r)}
                        style={{ border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize',
                          background: inviteRole === r ? T.accentDark : 'transparent', color: inviteRole === r ? 'var(--lyra-color-surface)' : T.ink3 }}>
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '4px 18px 14px' }}>
              {filteredCandidates.length === 0 ? (
                <div style={{ fontSize: 12, color: T.ink4, textAlign: 'center', padding: '20px 0' }}>No one to invite.</div>
              ) : filteredCandidates.map((c: any) => {
                const cid = String(c.userId || c._id);
                const selected = selectedInvites.has(cid);
                return (
                  <div key={cid} onClick={() => setSelectedInvites((prev) => { const n = new Set(prev); n.has(cid) ? n.delete(cid) : n.add(cid); return n; })}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 8px', borderRadius: 8, cursor: 'pointer', background: selected ? T.accentLight : 'transparent' }}>
                    <Avatar src={c.avatar || c.profilePicture} name={c.lyran_name || c.name || 'User'} size={28} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>{c.lyran_name || c.name}</div>
                    </div>
                    <div style={{ width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${selected ? T.accent : T.border}`, background: selected ? T.accent : 'var(--lyra-color-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--app-on-accent)', fontSize: 10 }}>
                      {selected && '✓'}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ padding: '12px 18px', borderTop: `1px solid ${T.border}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowInvite(false)} style={{ border: `1px solid ${T.border}`, background: T.surface, borderRadius: 8, padding: '7px 14px', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
              <button onClick={sendInvites} disabled={selectedInvites.size === 0 || invitingBusy}
                style={{ border: 'none', background: T.accentDark, color: 'var(--app-on-accent)', borderRadius: 8, padding: '7px 16px', fontSize: 12, fontWeight: 600, cursor: selectedInvites.size === 0 ? 'not-allowed' : 'pointer', opacity: selectedInvites.size === 0 ? 0.6 : 1 }}>
                {invitingBusy ? 'Sending…' : `Send invite${selectedInvites.size > 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddTask && spaceId && (
        <AddTaskModal
          isOpen={showAddTask}
          onClose={() => setShowAddTask(false)}
          onTaskAdded={() => { setShowAddTask(false); loadKanbanTasks(); }}
          mode="space-task"
          spaceId={spaceId}
        />
      )}
    </div>
  );
}
